import { Controller, useFormContext } from "react-hook-form";
import { IconButton, InputAdornment, TextField } from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { MouseEvent, useRef, useState } from "react";
import Popover from "@mui/material/Popover";
import { IMask } from "react-imask";
import dayjs from "dayjs";
import { textFieldDefaultProps } from "../Forms/TextFieldDefaultProps";
import { TIME_MASK_ZEROHOUR_ZEROMINUTES_24H } from "../Forms/Constants/TimeConstants";
import StaticWeekDaysTimePicker from "./StaticWeekDaysTimePicker";
import { TextMaskCustom } from "../Forms/TextMaskFacade";

interface WeekDaysTimePickerProps {
  fieldName: string;
  disabled: boolean;
  label: string;
}

export const WeekDaysTimePicker = ({
  fieldName,
  label,
  disabled = false,
}: WeekDaysTimePickerProps) => {
  const methods = useFormContext();
  const { control, setValue, watch } = methods;
  const openingTimeInputRef = useRef(null);
  const [showTimepicker, setShowTimepicker] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<HTMLInputElement | null>(null);
  const handleClockIconClick = () => {
    const newValue = !showTimepicker;
    setShowTimepicker(newValue);
    setAnchorEl(openingTimeInputRef.current);
  };

  const supressClockIconMousedown = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;
  const handleClose = () => {
    setAnchorEl(null);
  };

  const currentWeekDaysValue = watch("WeekDays");
  const currentTimeValue = watch(fieldName);
  return (
    <>
      <Controller
        name={fieldName}
        control={control}
        render={({ field, fieldState }) => (
          <TextField
            {...textFieldDefaultProps}
            ref={openingTimeInputRef}
            label={label}
            value={field.value.format(TIME_MASK_ZEROHOUR_ZEROMINUTES_24H) ?? ""}
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            disabled={disabled}
            placeholder={TIME_MASK_ZEROHOUR_ZEROMINUTES_24H}
            InputProps={{
              inputComponent: TextMaskCustom as any,
              inputProps: {
                mask: TIME_MASK_ZEROHOUR_ZEROMINUTES_24H,
                onAccept: (value: string) => {
                  const newValue = dayjs(value, TIME_MASK_ZEROHOUR_ZEROMINUTES_24H);
                  if (newValue.isValid()) field.onChange(newValue);
                },
                blocks: {
                  HH: {
                    mask: IMask.MaskedRange,
                    from: 0,
                    to: 23,
                    maxLength: 2,
                    autofix: "pad",
                  },
                  mm: {
                    mask: IMask.MaskedRange,
                    from: 0,
                    to: 59,
                    maxLength: 2,
                    autofix: "pad",
                  },
                },
              },
              style: { textTransform: "uppercase" },
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    disabled={disabled}
                    onClick={handleClockIconClick}
                    onMouseDown={supressClockIconMousedown}
                    edge="end"
                  >
                    <AccessTimeIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        )}
      />
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <StaticWeekDaysTimePicker
          onRequestClose={handleClose}
          weekDays={currentWeekDaysValue}
          setWeekDays={(newWeekDays) => setValue("WeekDays", newWeekDays, { shouldValidate: true, shouldDirty: true, shouldTouch: true })}
          timeValue={currentTimeValue}
          setTimeValue={(newTimeValue) => setValue(fieldName, newTimeValue)}
        />
      </Popover>
    </>
  );
};
